import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp, Loader2, AlertCircle, ChevronDown, ChevronUp,
  RefreshCw, Download, PieChart as PieChartIcon, Percent,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import { useAuth } from "@/lib/auth";

const TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "semana", label: "Patron semanal" },
  { id: "canal", label: "Por canal" },
  { id: "hora", label: "Por hora" },
  { id: "rentabilidad", label: "Rentabilidad" },
  { id: "productos", label: "Productos" },
];

const CHANNEL_COLORS = ["#FFDF00", "#1A1A1A", "#f97316", "#22c55e", "#3b82f6", "#a855f7"];

function fmt(n) { return n != null ? Number(n).toLocaleString("es-MX") : "—"; }
function fmtMXN(n) { return n != null ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0 })}` : "—"; }

function BranchSummaryRow({ row, isTop }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 ${isTop ? "bg-amber-50/50" : ""}`}>
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-800">{row.branch_name}</div>
        {row.mejor_dia && <div className="text-xs text-slate-400">Mejor dia: {row.mejor_dia}</div>}
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold text-slate-900">{fmtMXN(row.ventas)}</div>
        <div className="text-xs text-slate-500">{fmt(row.ordenes)} ordenes</div>
      </div>
      <div className="text-right w-20">
        <div className="text-sm font-medium text-slate-700">{fmtMXN(row.ticket_avg)}</div>
        <div className="text-xs text-slate-400">ticket prom</div>
      </div>
    </div>
  );
}

export default function OpsReportes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "corporate_admin";
  const [tab, setTab] = useState("resumen");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [dowData, setDowData] = useState([]);
  const [channelData, setChannelData] = useState(null);
  const [profitPeriod, setProfitPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [profitData, setProfitData] = useState(null);
  const [hourData, setHourData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/branches").then(r => {
      const bs = r.data.filter(b => !b.is_corporate);
      setBranches(bs);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.set("branch_id", selectedBranch);
      if (fromDate) params.set("from", fromDate);
      if (toDate) params.set("to", toDate);
      const ps = params.toString();

      const [sumRes, dowRes, chRes] = await Promise.all([
        api.get(`/ops/reports/summary?${ps}`),
        api.get(`/ops/reports/by-day-of-week?${ps}`),
        api.get(`/ops/reports/by-channel?${ps}`),
      ]);
      setSummary(sumRes.data);
      setDowData(dowRes.data);
      setChannelData(chRes.data);
    } catch {
      toast.error("Error al cargar reportes");
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadProfitability = useCallback(async () => {
    try {
      const params = new URLSearchParams({ period: profitPeriod });
      if (selectedBranch) params.set("branch_id", selectedBranch);
      const r = await api.get(`/ops/reports/profitability?${params.toString()}`);
      setProfitData(r.data);
    } catch {
      toast.error("Error al cargar rentabilidad");
    }
  }, [profitPeriod, selectedBranch]);

  useEffect(() => { if (tab === "rentabilidad") loadProfitability(); }, [tab, loadProfitability]);

  const _rangeParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedBranch) params.set("branch_id", selectedBranch);
    if (fromDate) params.set("from", fromDate);
    if (toDate) params.set("to", toDate);
    return params;
  }, [selectedBranch, fromDate, toDate]);

  const loadByHour = useCallback(async () => {
    try {
      const r = await api.get(`/ops/reports/by-hour?${_rangeParams().toString()}`);
      setHourData(r.data);
    } catch {
      toast.error("Error al cargar ventas por hora");
    }
  }, [_rangeParams]);

  useEffect(() => { if (tab === "hora") loadByHour(); }, [tab, loadByHour]);

  const loadProducts = useCallback(async () => {
    try {
      const params = _rangeParams().toString();
      const [topRes, abcRes] = await Promise.all([
        api.get(`/ops/reports/top-products?${params}&limit=20`),
        api.get(`/ops/reports/abc-analysis?${params}`),
      ]);
      setProductsData({ top: topRes.data, abc: abcRes.data });
    } catch {
      toast.error("Error al cargar reportes de productos");
    }
  }, [_rangeParams]);

  useEffect(() => { if (tab === "productos") loadProducts(); }, [tab, loadProducts]);

  const trendData = summary?.daily_trend?.reduce((acc, d) => {
    const existing = acc.find(x => x.date === d.date);
    if (existing) {
      existing.ventas = (existing.ventas || 0) + (d.ventas || 0);
      existing.ordenes = (existing.ordenes || 0) + (d.ordenes || 0);
    } else {
      acc.push({ date: d.date, ventas: d.ventas || 0, ordenes: d.ordenes || 0 });
    }
    return acc;
  }, []) || [];

  return (
    <div className="p-6 space-y-5" data-testid="reportes-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reportes de ventas</h1>
          <p className="text-sm text-slate-500">Analisis basado en cortes de caja — reportes por producto disponibles cuando Wansoft los exponga</p>
        </div>
        <button onClick={loadData} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={selectedBranch}
          onChange={e => setSelectedBranch(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
        >
          <option value="">Todas las sucursales</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
        <span className="text-slate-400 text-sm">a</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
      </div>

      {/* Totales rápidos */}
      {summary?.total && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Ventas totales", value: fmtMXN(summary.total.ventas) },
            { label: "Ordenes totales", value: fmt(summary.total.ordenes) },
            { label: "Ticket promedio", value: fmtMXN(summary.total.ticket_avg) },
            { label: "Dias con corte", value: summary.total.dias },
          ].map(card => (
            <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">{card.label}</div>
              <div className="text-2xl font-bold text-slate-900">{card.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-[#FFDF00] text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-48 animate-pulse" />)}</div>
      ) : (
        <>
          {/* RESUMEN */}
          {tab === "resumen" && (
            <div className="space-y-4">
              {/* Ranking sucursales */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700">Ventas por sucursal</span>
                </div>
                {summary?.summary_by_branch?.length === 0 ? (
                  <p className="text-sm text-slate-400 p-4">Sin datos en el periodo seleccionado</p>
                ) : (
                  summary?.summary_by_branch?.map((row, i) => (
                    <BranchSummaryRow key={row.branch_id} row={row} isTop={i === 0} />
                  ))
                )}
              </div>

              {/* Tendencia diaria */}
              {trendData.length > 1 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="text-sm font-semibold text-slate-700 mb-4">Tendencia diaria — ventas</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(v, name) => name === "ventas" ? [fmtMXN(v), "Ventas"] : [fmt(v), "Ordenes"]}
                        labelFormatter={l => `Fecha: ${l}`}
                      />
                      <Legend formatter={n => n === "ventas" ? "Ventas" : "Ordenes"} />
                      <Line type="monotone" dataKey="ventas" stroke="#FFDF00" strokeWidth={2} dot={false} name="ventas" />
                      <Line type="monotone" dataKey="ordenes" stroke="#94a3b8" strokeWidth={1.5} dot={false} name="ordenes" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* PATRON SEMANAL */}
          {tab === "semana" && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-700 mb-4">Promedio de ventas por dia de semana</div>
              {dowData.length === 0 ? (
                <p className="text-sm text-slate-400">Sin datos suficientes</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={dowData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(v, name) => {
                        if (name === "avg_ventas") return [fmtMXN(v), "Ventas prom"];
                        if (name === "avg_ordenes") return [fmt(v), "Ordenes prom"];
                        return [v, name];
                      }}
                    />
                    <Legend formatter={n => n === "avg_ventas" ? "Ventas prom" : "Ordenes prom"} />
                    <Bar yAxisId="left" dataKey="avg_ventas" fill="#FFDF00" radius={[4, 4, 0, 0]} name="avg_ventas" />
                    <Bar yAxisId="right" dataKey="avg_ordenes" fill="#cbd5e1" radius={[4, 4, 0, 0]} name="avg_ordenes" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <p className="text-xs text-slate-400 mt-3">
                Basado en {dowData[0]?.data_points || 0}+ cortes historicos. Mas cortes = patron mas preciso.
              </p>
            </div>
          )}

          {/* POR CANAL */}
          {tab === "canal" && (
            <div className="space-y-4" data-testid="reportes-canal-tab">
              {!channelData || channelData.by_channel?.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">
                  Sin datos de canal en el periodo seleccionado
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                        <PieChartIcon className="w-4 h-4 text-slate-400" /> Distribucion por canal
                      </div>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={channelData.by_channel} dataKey="total" nameKey="label" cx="50%" cy="50%" outerRadius={85}
                            label={({ label, pct }) => `${label} ${pct}%`}>
                            {channelData.by_channel.map((entry, i) => (
                              <Cell key={entry.channel} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={v => fmtMXN(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Total por canal</div>
                      {channelData.by_channel.map((c, i) => (
                        <div key={c.channel} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`canal-row-${c.channel}`}>
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                          <span className="text-sm text-slate-700 flex-1">{c.label}</span>
                          <span className="text-sm font-semibold text-slate-900">{fmtMXN(c.total)}</span>
                          <span className="text-xs text-slate-400 w-12 text-right">{c.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Detalle por sucursal y canal</div>
                    {channelData.by_branch_channel?.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 text-sm">
                        <span className="text-slate-700 flex-1">{r.branch_name}</span>
                        <span className="text-slate-500 w-28">{r.label}</span>
                        <span className="font-medium text-slate-900">{fmtMXN(r.total)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* POR HORA */}
          {tab === "hora" && (
            <div className="space-y-4" data-testid="reportes-hora-tab">
              {!hourData || hourData.data_source === "unavailable" ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="reportes-hora-empty">
                  Sin datos de ventas por hora para este periodo
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="reportes-hora-source">
                    <span>Fuente: {hourData.source === "wansoft" ? "POS Wansoft" : (hourData.source || "—")}</span>
                    <span>· {hourData.days} dia(s) con datos</span>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-sm font-semibold text-slate-700 mb-4">Curva de ventas por hora</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={hourData.curve}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={h => `${h}h`} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip
                          formatter={(v, name) => name === "amount" ? [fmtMXN(v), "Ventas"] : [fmt(v), "Ordenes"]}
                          labelFormatter={h => `Hora: ${h}:00`}
                        />
                        <Bar dataKey="amount" fill="#FFDF00" radius={[4, 4, 0, 0]} name="amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="reportes-hora-pico">
                      <div className="text-xs text-slate-500 mb-1">Hora pico</div>
                      <div className="text-xl font-bold text-slate-900">
                        {hourData.peak_hour ? `${hourData.peak_hour.hour}:00` : "—"}
                      </div>
                      <div className="text-xs text-slate-500">{hourData.peak_hour ? fmtMXN(hourData.peak_hour.amount) : ""}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="reportes-hora-valle">
                      <div className="text-xs text-slate-500 mb-1">Hora valle</div>
                      <div className="text-xl font-bold text-slate-900">
                        {hourData.valley_hour ? `${hourData.valley_hour.hour}:00` : "—"}
                      </div>
                      <div className="text-xs text-slate-500">{hourData.valley_hour ? fmtMXN(hourData.valley_hour.amount) : ""}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* RENTABILIDAD */}
          {tab === "rentabilidad" && (
            <div className="space-y-4" data-testid="reportes-rentabilidad-tab">
              <div className="flex items-center gap-3">
                <Percent className="w-4 h-4 text-slate-400" />
                <label className="text-sm text-slate-600">Periodo (mes):</label>
                <input type="month" value={profitPeriod} onChange={e => setProfitPeriod(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  data-testid="rentabilidad-period-input" />
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{profitData?.note || "Margen aproximado — se estima con el % de Costo de Alimentos capturado por el gerente de sucursal."}</span>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Sucursal</th>
                      <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Ventas</th>
                      <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Costo alimentos %</th>
                      <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Costo estimado</th>
                      <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Margen aprox.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(profitData?.branches || []).map(b => (
                      <tr key={b.branch_id} className="border-b border-slate-100 last:border-0" data-testid={`rentabilidad-row-${b.branch_id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{b.branch_name}</td>
                        <td className="px-4 py-2.5 text-right">{fmtMXN(b.ventas)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {b.data_available ? `${b.costo_alimentos_pct}%` : <span className="text-slate-400">Sin captura este mes</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">{b.data_available ? fmtMXN(b.costo_alimentos_estimado) : "—"}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
                          {b.data_available ? `${fmtMXN(b.margen_bruto_aproximado)} (${b.margen_pct_aproximado}%)` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PRODUCTOS */}
          {tab === "productos" && (
            <div className="space-y-4" data-testid="reportes-productos-tab">
              {!productsData || productsData.top?.data_source === "unavailable" ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="reportes-productos-empty">
                  Sin datos de ventas por producto todavia. Se pueblan con el sync de ventas de Wansoft.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Top productos</div>
                    {(productsData.top?.products || []).map((p, i) => (
                      <div key={p.product_code || i} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 text-sm" data-testid={`top-producto-row-${i}`}>
                        <span className="text-slate-400 w-6">{i + 1}</span>
                        <span className="text-slate-700 flex-1 truncate">{p.product_name}</span>
                        <span className="text-slate-500 shrink-0">{fmt(p.units_sold)} u.</span>
                        <span className="font-semibold text-slate-900 w-24 text-right shrink-0">{fmtMXN(p.revenue)}</span>
                      </div>
                    ))}
                    {(productsData.top?.products || []).length === 0 && (
                      <p className="text-sm text-slate-400 p-4">Sin datos en el periodo seleccionado</p>
                    )}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Clasificacion ABC</div>
                    {(productsData.abc?.abc || []).map((p, i) => (
                      <div key={p.product_code || i} className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 last:border-0 text-sm" data-testid={`abc-producto-row-${i}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          p.class === "A" ? "bg-emerald-100 text-emerald-700" : p.class === "B" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                        }`}>{p.class}</span>
                        <span className="text-slate-700 flex-1 truncate">{p.product_name}</span>
                        <span className="font-medium text-slate-900 shrink-0">{fmtMXN(p.revenue)}</span>
                      </div>
                    ))}
                    {(productsData.abc?.abc || []).length === 0 && (
                      <p className="text-sm text-slate-400 p-4">Sin datos en el periodo seleccionado</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
